/**
 * Detection / emission totals under the current filters (brief §8).
 *
 * Two modes:
 *  - "country": count the flat records that match the applied filters + legend
 *    grades, across the whole dataset.
 *  - "extent": count what's actually rendered in the current viewport, via
 *    queryRenderedFeatures on the plume layer — which already carries every
 *    filter — deduped by detection id. Recomputes when the map moves.
 */
import { useMemo } from "react";

import { useDataStore } from "../store/dataStore";
import { useFiltersStore } from "../store/filtersStore";
import { useLayersStore } from "../store/layersStore";
import { LYR } from "../map/dataLayers";
import { recordMatches } from "../lib/filterMatch";

export interface Stats {
  count: number;
  totalRate: number;
}

export function useStats(): Stats {
  const records = useDataStore((s) => s.records);
  const map = useDataStore((s) => s.map);
  const viewportVersion = useDataStore((s) => s.viewportVersion);
  const applied = useFiltersStore((s) => s.applied);
  const countMode = useFiltersStore((s) => s.countMode);
  const grades = useLayersStore((s) => s.grades);

  return useMemo<Stats>(() => {
    if (countMode === "extent") {
      if (!map || !map.getLayer(LYR.plumeFill)) return { count: 0, totalRate: 0 };
      const seen = new Map<string, number | null>();
      for (const f of map.queryRenderedFeatures({ layers: [LYR.plumeFill] })) {
        const p = f.properties as Record<string, unknown>;
        const id = String(p.detectionId);
        if (!seen.has(id)) seen.set(id, (p.emissionRate as number | null) ?? null);
      }
      let total = 0;
      seen.forEach((rate) => (total += rate ?? 0));
      return { count: seen.size, totalRate: total };
    }

    let count = 0;
    let total = 0;
    for (const rec of records) {
      if (!recordMatches(rec, applied, grades)) continue;
      count += 1;
      total += rec.emissionRate ?? 0;
    }
    return { count, totalRate: total };
    // viewportVersion is intentionally a dependency: it changes on map move.
  }, [countMode, records, map, viewportVersion, applied, grades]);
}
